import { useEffect, useState } from "react";
import {
  ApiError,
  accessApi,
  sourcesApi,
  type AccessGrant,
  type AccessGroup,
} from "../../services/api";

/**
 * Shares one dashboard or document with a group, or with every group that
 * already has a role on the project behind it.
 */
export function SharePanel({
  resourceType,
  resourceId,
  contextId = "",
}: {
  resourceType: "dashboard" | "artifact";
  resourceId: string;
  contextId?: string;
}) {
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [grants, setGrants] = useState<AccessGrant[]>([]);
  const [groupId, setGroupId] = useState("");
  const [role, setRole] = useState<"viewer" | "editor">("viewer");
  const [projectId, setProjectId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    const [listed, current, sources] = await Promise.all([
      accessApi.groups(),
      accessApi.grants(resourceType, resourceId),
      contextId ? sourcesApi.list().catch(() => []) : Promise.resolve([]),
    ]);
    setGroups(listed);
    setGrants(current);
    setGroupId(listed[0]?.id ?? "");
    const source = sources.find((item) => item.id === contextId);
    setProjectId(source?.sourceProjectId ?? "");
  }

  useEffect(() => {
    load().catch((exc: unknown) => {
      setError(exc instanceof ApiError ? exc.message : "Sharing could not be loaded.");
    });
  }, [resourceId]);

  async function grant() {
    setError("");
    setNotice("");
    try {
      await accessApi.setGrant({
        granteeType: "group",
        granteeId: groupId,
        resourceType,
        resourceId,
        role,
      });
      setNotice("Shared.");
      await load();
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : "This was not shared.");
    }
  }

  async function shareProject() {
    setError("");
    setNotice("");
    try {
      await accessApi.shareWithProject({ projectId, resourceType, resourceId, role });
      setNotice("Shared with the project's groups.");
      await load();
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : "This was not shared.");
    }
  }

  const names = Object.fromEntries(groups.map((group) => [group.id, group.name]));

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <h2>Share</h2>
          <p>
            People you share this with can read the saved figures. Refreshing
            them still needs a role on the project.
          </p>
        </div>
      </div>
      {error && <p className="inline-notice warning">{error}</p>}
      {notice && <p className="inline-notice">{notice}</p>}
      <ul>
        {grants.filter((grant) => grant.granteeType === "group").map((grant) => (
          <li key={grant.id}>
            {names[grant.granteeId] || grant.granteeId} — {grant.role}{" "}
            <button
              type="button"
              className="button tertiary"
              onClick={() => accessApi.removeGrant(grant.id).then(load)}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="admin-actions">
        <select value={groupId} onChange={(event) => setGroupId(event.target.value)} aria-label="Group">
          {groups.map((group) => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </select>
        <select
          value={role}
          onChange={(event) => setRole(event.target.value as "viewer" | "editor")}
          aria-label="Role"
        >
          <option value="viewer">Viewer</option>
          <option value="editor">Editor</option>
        </select>
        <button type="button" className="button primary" onClick={() => void grant()} disabled={!groupId}>
          Share with group
        </button>
        {projectId && (
          <button type="button" className="button tertiary" onClick={() => void shareProject()}>
            Share with this project's groups
          </button>
        )}
      </div>
    </section>
  );
}
